import React, { useEffect, useRef, useState } from "react";
import "./S2VI.css";

import S2VI from "../../assets/S2VI1.png";
import S2VI2 from "../../assets/S2VI2.png";
import S2VI3 from "../../assets/S2VI3b.png";
import S2VI4 from "../../assets/S2VI2sqaure.png";

export default function Section2() {
  // --- refs & state for mobile slider ---
  const snapRef = useRef(null);
  const dotsRef = useRef([]);
  const [current, setCurrent] = useState(0);

  const getSlides = () =>
    Array.from(snapRef.current?.querySelectorAll(".vs2-slide") ?? []);

  const getPadLeft = () =>
    parseFloat(getComputedStyle(snapRef.current).paddingLeft) || 0;

  // Smooth scroll that temporarily disables snap so the browser doesn't "correct" our centering
  const smoothScrollTo = (el, left) => {
    if (!el) return;
    const prevSnap = el.style.scrollSnapType;
    el.style.scrollSnapType = "none";
    el.scrollTo({ left, behavior: "smooth" });
    // restore after the smooth scroll likely ends
    clearTimeout(el._snapTimer);
    el._snapTimer = setTimeout(() => {
      el.style.scrollSnapType = prevSnap || "";
    }, 450);
  };

  // Manual centering to avoid edge snap issues (works even for last slide)
  const centerSlide = (idx) => {
    const scroller = snapRef.current;
    if (!scroller) return;
    const slides = getSlides();
    const n = slides.length;
    if (!n) return;

    // wrap-around index
    const targetIdx = ((idx % n) + n) % n;
    const target = slides[targetIdx];

    const padLeft = getPadLeft();
    const scrollerWidth = scroller.clientWidth;
    const slideWidth = target.offsetWidth;

    const left =
      target.offsetLeft - padLeft - (scrollerWidth - slideWidth) / 2;

    smoothScrollTo(scroller, left);
  };

  const goTo = (idx) => centerSlide(idx);
  const goPrev = () => centerSlide(current - 1); // loops via modulo in centerSlide
  const goNext = () => centerSlide(current + 1);

  // Keep dots + current index in sync while user swipes
  useEffect(() => {
    const scroller = snapRef.current;
    if (!scroller) return;

    const onScroll = () => {
      const slides = getSlides();
      if (!slides.length) return;

      const padLeft = getPadLeft();
      const viewportCenter = scroller.scrollLeft + padLeft + scroller.clientWidth / 2;

      // pick slide whose center is closest to viewport center
      let bestIdx = 0;
      let bestDist = Infinity;
      slides.forEach((s, i) => {
        const slideCenter = s.offsetLeft + s.offsetWidth / 2;
        const dist = Math.abs(slideCenter - viewportCenter);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      });

      if (bestIdx !== current) setCurrent(bestIdx);

      // update dots aria/active
      dotsRef.current.forEach((d, idx) => {
        if (!d) return;
        d.classList.toggle("is-active", idx === bestIdx);
        d.setAttribute("aria-selected", String(idx === bestIdx));
      });
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [current]);

  return (
    <section className="vendor-s2">
      {/* ===== Desktop / Tablet (unchanged) ===== */}
      <div className="vs2-wrap">
        {/* Row 1 */}
        <div className="vs2-row">
          <div className="vs2-col vs2-text">
            <h2 className="vs2-h2">New retail customers</h2>
            <p className="vs2-p">
              Boost your business to hundreds of thousands of customers across Canada
            </p>
          </div>
          <div className="vs2-col vs2-media">
            <img src={S2VI} alt="New retail customers" />
          </div>
        </div>

        {/* Row 2 (image left, text right) */}
        <div className="vs2-row vs2-alt">
          <div className="vs2-col vs2-media-alt">
            <img src={S2VI2} alt="Secure, on-time payments" />
          </div>
          <div className="vs2-col vs2-text">
            <h2 className="vs2-h2-alt">Secure, on-time payments</h2>
            <p className="vs2-p-alt">
              We manage payment processing, pay you directly, and cover 60-day
              payment terms for your eligible customers.
            </p>
          </div>
        </div>

        {/* Row 3 */}
        <div className="vs2-row">
          <div className="vs2-col vs2-text">
            <h2 className="vs2-h2">Saved time and money</h2>
            <p className="vs2-p">
              Grow efficiently with easy order management, invoicing, and
              fulfillment tools built for retail.
            </p>
          </div>
          <div className="vs2-col vs2-media">
            <img src={S2VI3} alt="Saved time and money" />
          </div>
        </div>
      </div>

      {/* ===== Mobile (<780px): slider + arrows + dots + list ===== */}
      <div className="vs2-mobile" aria-label="Highlights carousel">
        <div className="vs2-slider">
          <button
            type="button"
            className="vs2-nav vs2-prev"
            aria-label="Previous slide"
            onClick={goPrev}
          >
            ‹
          </button>

          <div className="vs2-snap" ref={snapRef} aria-live="polite">
            <figure className="vs2-slide">
              <img src={S2VI} alt="New retail customers" />
            </figure>
            <figure className="vs2-slide">
              <img src={S2VI4} alt="Secure, on-time payments" />
            </figure>
            <figure className="vs2-slide">
              <img src={S2VI3} alt="Saved time and money" />
            </figure>
          </div>

          <button
            type="button"
            className="vs2-nav vs2-next"
            aria-label="Next slide"
            onClick={goNext}
          >
            ›
          </button>
        </div>

        <div className="vs2-dots" role="tablist" aria-label="Slide dots">
          {[0, 1, 2].map((i) => (
            <button
              key={i}
              ref={(el) => (dotsRef.current[i] = el)}
              role="tab"
              className={`vs2-dot ${i === 0 ? "is-active" : ""}`}
              aria-selected={i === 0 ? "true" : "false"}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>

        {/* Numbered list (unchanged content) */}
        <ol className="vs2-list">
          <li className="vs2-li">
            <span className="vs2-num">01</span>
            <div className="vs2-li-body">
              <h3 className="vs2-li-title">New retail customers</h3>
              <p className="vs2-li-text">
                Boost your business to hundreds of thousands of customers across Canada
              </p>
            </div>
          </li>

          <li className="vs2-li">
            <span className="vs2-num">02</span>
            <div className="vs2-li-body">
              <h3 className="vs2-li-title">Secure, on-time payments</h3>
              <p className="vs2-li-text">
                We manage payment processing, pay you directly, and cover 60-day
                payment terms for your eligible customers.
              </p>
            </div>
          </li>

          <li className="vs2-li">
            <span className="vs2-num">03</span>
            <div className="vs2-li-body">
              <h3 className="vs2-li-title">Saved time and money</h3>
              <p className="vs2-li-text">
                Grow efficiently with easy order management, invoicing, and
                fulfillment tools built for retail.
              </p>
            </div>
          </li>
        </ol>
      </div>
    </section>
  );
}
